import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface TaskTimerInstance {
  taskId: string;
  taskTitle: string;
  projectId: string;
  categoryId: string | null;
  categoryName: string | null;
  subprojectId: string | null;
  isRunning: boolean;
  isPaused: boolean;
  startTime: number | null;      // ms timestamp when last resumed
  pausedTime: number | null;     // ms timestamp when paused
  elapsedSeconds: number;        // accumulated seconds across pause/resume cycles
}

interface TaskTimerState {
  timers: Record<string, TaskTimerInstance>;

  startTimer: (task: {
    id: string;
    title: string;
    project_id: string;
    category_id?: string | null;
    category_name?: string | null;
    subproject_id?: string | null;
  }) => void;

  pauseTimer: (taskId: string) => void;
  resumeTimer: (taskId: string) => void;

  stopTimer: (taskId: string) => {
    hours: number;
    taskId: string;
    taskTitle: string;
    projectId: string;
    categoryId: string | null;
    categoryName: string | null;
    subprojectId: string | null;
  } | null;

  discardTimer: (taskId: string) => void;
  getElapsed: (taskId: string) => number;  // seconds
  getTimerState: (taskId: string) => 'idle' | 'running' | 'paused';
}

export const useTaskTimerStore = create<TaskTimerState>()(
  persist(
    (set, get) => ({
      timers: {},

      startTimer: (task) => {
        set(state => ({
          timers: {
            ...state.timers,
            [task.id]: {
              taskId: task.id,
              taskTitle: task.title,
              projectId: task.project_id,
              categoryId: task.category_id ?? null,
              categoryName: task.category_name ?? null,
              subprojectId: task.subproject_id ?? null,
              isRunning: true,
              isPaused: false,
              startTime: Date.now(),
              pausedTime: null,
              elapsedSeconds: 0,
            },
          },
        }));
      },

      pauseTimer: (taskId) => {
        const timer = get().timers[taskId];
        if (!timer || !timer.isRunning || timer.isPaused) return;

        const now = Date.now();
        const additional = timer.startTime
          ? Math.floor((now - timer.startTime) / 1000)
          : 0;

        set(s => ({
          timers: {
            ...s.timers,
            [taskId]: {
              ...timer,
              isPaused: true,
              pausedTime: now,
              elapsedSeconds: timer.elapsedSeconds + additional,
              startTime: null,
            },
          },
        }));
      },

      resumeTimer: (taskId) => {
        const timer = get().timers[taskId];
        if (!timer || !timer.isRunning || !timer.isPaused) return;

        set(s => ({
          timers: {
            ...s.timers,
            [taskId]: {
              ...timer,
              isPaused: false,
              startTime: Date.now(),
              pausedTime: null,
            },
          },
        }));
      },

      stopTimer: (taskId) => {
        const state = get();
        const timer = state.timers[taskId];
        if (!timer) return null;

        const elapsed = state.getElapsed(taskId);
        const hours = elapsed > 0 ? Math.ceil((elapsed / 3600) * 4) / 4 : 0;

        const result = {
          hours,
          taskId: timer.taskId,
          taskTitle: timer.taskTitle,
          projectId: timer.projectId,
          categoryId: timer.categoryId,
          categoryName: timer.categoryName,
          subprojectId: timer.subprojectId,
        };

        const { [taskId]: _removed, ...rest } = state.timers;
        set({ timers: rest });

        return result;
      },

      discardTimer: (taskId) => {
        const { [taskId]: _removed, ...rest } = get().timers;
        set({ timers: rest });
      },

      getElapsed: (taskId) => {
        const timer = get().timers[taskId];
        if (!timer || !timer.isRunning) return 0;
        if (timer.isPaused) return timer.elapsedSeconds;
        if (timer.startTime) {
          return timer.elapsedSeconds + Math.floor((Date.now() - timer.startTime) / 1000);
        }
        return timer.elapsedSeconds;
      },

      getTimerState: (taskId) => {
        const timer = get().timers[taskId];
        if (!timer || !timer.isRunning) return 'idle';
        if (timer.isPaused) return 'paused';
        return 'running';
      },
    }),
    { name: 'mytime-task-timers' }
  )
);

export const formatElapsed = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
